<script>
import {ProductType} from "@/libs/product";
import { BCardText, BCard, BRow, BCol, BFormGroup, BButton, BCardGroup, BTable, BCollapse } from 'bootstrap-vue'
import {SupplierSelector} from "@core/components/supply-chain";
import {Button} from "@core/components/common";
import Overlay from "@core/components/common/Overlay.vue";
import {ProductProvider} from "@core/domain/product";
import InstructionText from "@core/components/common/instructions/InstructionText.vue";

export default {
    components: {
        InstructionText,
        Overlay, SupplierSelector, BCardText, BCard, BRow, BCol, BFormGroup, BButton, BCardGroup, Button, BTable,
        BCollapse
    },
    props: {
        product: {
            default() {
                return {type: null, names: [], identifiers: []}
            }
        }
    },
    data() {
        return {
            type: null,
            types: ProductType,
            suppliers: null,
            showLoading: false,
            managed: true,
            productTableItems: []
        }
    },
    methods: {
        isTypeSelected(type) {
            return this.type && this.type === type;
        },
        selectProductType(type) {
            this.type = type;
        },
        suppliersChange(idList, suppliers) {
            this.suppliers = suppliers;
        },
        save() {
            this.managed = false;
            this.showLoading = true;
            const product = ProductProvider(this.product.id);
            product
                .getCard()
                .then(() => {
                    product
                        .convertType(this.type)
                        .then(() => {
                            if (this.isPurchased) {
                                product.setSuppliers(this.suppliers);
                                return product.saveCard();
                            }
                        })
                        .then(() => {
                            if (this.isPurchased) {
                                this.$emit('suppliers', this.suppliers);
                            }
                            this.managed = true;
                        })
                        .finally(() => {
                            this.showLoading = false;
                            this.$emit('done');
                        })
                    ;
                });
        }
    },
    computed: {
        productTitle() {
            if (this.product && this.product.names.length > 0) {
                return this.product.names[0].value;
            }

            return '';
        },
        productIdentifier() {
            if (this.product && this.product.identifiers.length > 0) {
                return this.product.identifiers[0].value;
            }

            return '';
        },
        isUndefined() {
            return this.product && this.product.type === ProductType.UNDEFINED;
        },
        isPurchased() {
            return this.type === ProductType.PURCHASED;
        },
        isProceedDisabled() {
            return !this.type || (this.type === ProductType.PURCHASED && (!this.suppliers || 0 === this.suppliers.length));
        }
    },
    created() {
        this.suppliers = null;
        this.managed = true;
        this.productTableItems = [];
    },
    watch: {
        product() {
            const item = {}
            item[this.$t('Page(NewProduct):Component(Text):product-name')] = this.productTitle;
            item[this.$t('Page(NewProduct):Component(Text):product-id')] = this.productIdentifier;
            this.productTableItems = [item];
        }
    }
}
</script>

<template>
    <Overlay :show="showLoading">
        <div v-if="!isUndefined && managed">
            <slot/>
        </div>
        <div v-if="isUndefined || !managed">
            <div class="text-center mb-2">
                <h1>{{ $t('Component(ProductTypeDefinition):label') }}</h1>
            </div>
            <b-row>
                <b-col
                    sm="10"
                    md="12"
                    lg="10"
                    class="mx-auto pl-4 pr-4"
                >
                    <InstructionText>{{ $t('Component(ProductTypeDefinition):instruction') }}</InstructionText>

                    <b-card :title="$t('Page(SentRequest):Component(Text):product-information')" no-body>
                        <b-table
                            responsive
                            :items="productTableItems"
                        />
                    </b-card>
                </b-col>
            </b-row>
            <b-row class="pricing-card text-center">
                <b-col
                    sm="10"
                    md="12"
                    lg="10"
                    class="mx-auto pl-4 pr-4"
                >
                    <b-row class="justify-content-md-center">
                        <b-col>
                            <b-card-group deck>
                                <b-card class="popular" align="center">
                                    <h3>{{ $t('Page(NewProduct):Component(Text):own-product-label') }}</h3>
                                    <div class="d-flex justify-content-between flex-column">
                                        <div class="mt-2 mb-2 text-center w-100">
                                            {{ $t('Page(NewProduct):Component(Text):own-product-description') }}
                                        </div>

                                        <div class="w-100 mt-auto">
                                            <b-button
                                                block
                                                variant="primary"
                                                v-if="!isTypeSelected(types.OWN)"
                                                @click="selectProductType(types.OWN)"
                                            >
                                                {{ $t('Page(NewProduct):Component(Button):own-product-select') }}
                                            </b-button>
                                            <b-button
                                                variant="outline-success"
                                                class="btn-icon rounded-circle"
                                                v-if="isTypeSelected(types.OWN)"
                                            >
                                                <feather-icon icon="CheckIcon" />
                                            </b-button>
                                        </div>
                                    </div>

                                </b-card>
                                <b-card align="center">
                                    <h3>{{ $t('Page(NewProduct):Component(Text):purchased-product-label') }}</h3>

                                    <div class="d-flex justify-content-around align-items-end flex-column">
                                        <div class="mt-2 mb-2 text-center w-100">
                                            {{ $t('Page(NewProduct):Component(Text):purchased-product-description') }}
                                        </div>

                                        <div class="w-100">
                                            <!-- buttons -->
                                            <b-button
                                                block
                                                variant="primary"
                                                v-if="!isTypeSelected(types.PURCHASED)"
                                                @click="selectProductType(types.PURCHASED)"
                                            >
                                                {{ $t('Page(NewProduct):Component(Button):purchased-product-select') }}
                                            </b-button>
                                            <b-button
                                                variant="outline-success"
                                                class="btn-icon rounded-circle"
                                                v-if="isTypeSelected(types.PURCHASED)"
                                            >
                                                <feather-icon icon="CheckIcon" />
                                            </b-button>
                                        </div>
                                    </div>
                                </b-card>
                            </b-card-group>
                        </b-col>
                    </b-row>
                </b-col>
            </b-row>
            <b-collapse :visible="isPurchased">
                <b-row>
                    <b-col
                        sm="10"
                        md="12"
                        lg="10"
                        class="mx-auto pl-4 pr-4"
                    >
                        <SupplierSelector
                            class="mt-2"
                            @change="suppliersChange"
                        />
                    </b-col>
                </b-row>
            </b-collapse>

            <b-row>
                <b-col class="text-center mt-2">
                    <Button
                        :disabled="isProceedDisabled"
                        @click="save"
                    >
                        {{ $t('Component(Button):save-and-proceed') }}
                    </Button>
                </b-col>
            </b-row>
        </div>
    </Overlay>
</template>
